<template>
  <div v-if="isLoaded">
    <h1>{{ $t("nav.title.PcqDsis") }}</h1>

    <v-card class="ma-5">
      <v-card-title>{{ $t("commons.signature") }} ({{ selectedSignature.section }})</v-card-title>
      <v-card-text>
        <p>
          <strong>{{ $t("Step.") }}:</strong> {{ pcqDetails.order }}
        </p>
        <p>
          <strong>{{ $t("Task") }}:</strong>
          <span v-if="pcqDetails.task && pcqDetails.task.name">{{ pcqDetails.task.name }}</span>
          <span v-else>{{ pcqDetails.name }}</span>
        </p>

        <p>
          <strong>{{ $t("ITNS (Y/N)") }}:</strong> {{ pcqDetails.pia }}
        </p>

        <p>
          <strong>{{ $t("Applicable documents") }}:</strong>
          {{ pcqDetails.applicable_document }}
        </p>

        <p>
          <strong>{{ $t("Doc rev.") }}:</strong> {{ pcqDetails.doc_rev }}
        </p>

        <p>
          <strong>{{ $t("Record (Y/N)") }}:</strong> {{ pcqDetails.record }}
        </p>

        <p>
          <strong>{{ $t("Record reference") }}:</strong> {{ pcqDetails.reference }}
        </p>

        <p>
          <strong>{{ $t("NCR/DR Observation") }}:</strong> {{ pcqDetails.observation }}
        </p>

        <p>
          <strong>{{ $t("A - Quality Point") }}:</strong> {{ pcqDetails.quality_point_a }}
        </p>
        <p>
          <strong>{{ $t("B - Quality Point") }}:</strong> {{ pcqDetails.quality_point_b }}
        </p>
        <p>
          <strong>{{ $t("C - Quality Point") }}:</strong> {{ pcqDetails.quality_point_c }}
        </p>
        <p>
          <strong>{{ $t("D - Quality Point") }}:</strong> {{ pcqDetails.quality_point_d }}
        </p>
        <!-- Autres champs ici -->

        <!-- Indication de la signature déjà effectuée -->
        <p v-if="selectedSignature && selectedSignature.sign_date">
          <strong>{{ $t("commons.sign_at") }}:</strong>
          {{ selectedSignature.sign_date }}
        </p>
      </v-card-text>
      <v-card-actions>
        <v-btn
          color="primary"
          @click="openSignatureModal"
          :disabled="!!(selectedSignature && selectedSignature.sign_date)"
        >
          {{ selectedSignature && selectedSignature.sign_date ? $t("signed") : $t("sign here") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Modal pour la signature -->
    <v-dialog
      v-model="isSignatureModalOpen"
      max-width="500px"
    >
      <v-card>
        <v-card-title>{{ $t("commons.signature") }}</v-card-title>
        <v-card-text>
          <!-- Champs pour le nom et prénom -->
          <v-text-field
            label="First Name"
            v-model="firstName"
            required
          ></v-text-field>
          <v-text-field
            label="Last Name"
            v-model="lastName"
            required
          ></v-text-field>

          <!-- Zone de signature (canvas) -->
          <canvas
            ref="signatureCanvas"
            width="450"
            height="200"
            class="signature-canvas"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="stopDrawing"
            @mouseleave="stopDrawing"
          ></canvas>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            @click="saveSignature"
            :disabled="!firstName || !lastName"
          >
            {{ $t("commons.sign") }}
          </v-btn>
          <v-btn
            color="secondary"
            @click="clearSignature"
          >
            {{ $t("commons.clear") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>

  <!-- Afficher un message de chargement si les données ne sont pas encore chargées -->
  <v-alert
    v-else
    type="info"
    class="ma-5"
  >
    {{ $t("messages.loading") }}
  </v-alert>
</template>

<script>
import { PcqDsiService } from "@/services"
import { mkPcqDsi } from "@/utils/datamodels"

export default {
  data() {
    return {
      pcqDetails: mkPcqDsi(),
      selectedSignature: null, // Stocke la signature associée à l'external_code
      isSignatureModalOpen: false,
      loading: false,
      isLoaded: false,
      firstName: "", // Nouveau champ pour le prénom
      lastName: "", // Nouveau champ pour le nom
      timezone: ""
    }
  },
  computed: {
    id() {
      return this.$route.params.id || null
    }
  },
  created() {
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    this.getPcqDetails()
  },
  methods: {
    getPcqDetails() {
      this.loading = true
      PcqDsiService.getPublicPcqDsi(this.id)
        .then(response => {
          this.pcqDetails = response
          this.isLoaded = true

          // Rechercher la signature correspondante par external_code
          const externalCode = this.id
          this.selectedSignature = this.pcqDetails.signatures.find(
            signature => signature.external_code === externalCode
          )
        })
        .catch(error => {
          console.error("Erreur lors de la récupération des détails:", error)
          this.isLoaded = false
        })
        .finally(() => {
          this.loading = false
        })
    },
    openSignatureModal() {
      this.isSignatureModalOpen = true
      this.$nextTick(() => {
        const canvas = this.$refs.signatureCanvas
        this.context = canvas.getContext("2d")
        this.context.lineWidth = 2
        this.context.lineCap = "round"
        this.context.strokeStyle = "black"
      })
    },
    startDrawing(event) {
      this.isDrawing = true
      this.context.beginPath()
      this.context.moveTo(event.offsetX, event.offsetY)
    },
    draw(event) {
      if (!this.isDrawing) return
      this.context.lineTo(event.offsetX, event.offsetY)
      this.context.stroke()
    },
    stopDrawing() {
      if (!this.isDrawing) return
      this.isDrawing = false
      this.context.closePath()
    },
    saveSignature() {
      const canvas = this.$refs.signatureCanvas
      const signatureImageDataURL = canvas.toDataURL("image/png")
      const signatureImageBlob = this.dataURLtoBlob(signatureImageDataURL)

      PcqDsiService.signExternal(this.pcqDetails.id, this.id, signatureImageBlob, {
        firstName: this.firstName,
        lastName: this.lastName,
        timezone: this.timezone
      })
        .then(() => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.saved", { entity: this.$t("commons.signature") }),
            color: "success"
          })
          this.isSignatureModalOpen = false
          this.getPcqDetails() // Met à jour les détails après la signature
        })
        .catch(error => {
          console.error("Error saving signature:", error)
        })
    },
    clearSignature() {
      const canvas = this.$refs.signatureCanvas
      const context = canvas.getContext("2d")
      context.clearRect(0, 0, canvas.width, canvas.height)
    },
    dataURLtoBlob(dataURL) {
      const byteString = atob(dataURL.split(",")[1])
      const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0]
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      return new Blob([ab], { type: mimeString })
    }
  }
}
</script>

<style>
.signature-canvas {
  border: 1px solid #ccc;
  cursor: crosshair;
}
</style>
